import { z } from 'zod';
import { contactPhoneSchema } from './common.schemas';

/** Loose-cargo-specific job data captured at intake (PRD → Job intake → Captured per job type (Loose cargo); gitaumoses4/chukua#32). */

const codeSchema = z.string().trim().min(1).max(32);

/** Who loads or offloads the cargo — a deployment's own words aren't in the PRD, so this is a closed set of three, same idiom as `RELEASE_STATUSES`. */
export const LOOSE_CARGO_RESPONSIBILITIES = ['customer', 'us', 'third_party'] as const;
export type LooseCargoResponsibility = (typeof LOOSE_CARGO_RESPONSIBILITIES)[number];

/** `PUT /jobs/:id/loose-cargo-details` — one row per job. `commodity` is mandatory; everything else fills in as it's learned, same as the job's own quick-create. */
export const looseCargoDetailInputSchema = z.object({
  commodity: z.string().trim().min(1).max(160),
  weightKg: z.number().int().positive().optional(),
  volumeM3: z.number().positive().optional(),
  packaging: z.string().trim().min(1).max(120).optional(),
  handlingNotes: z.string().trim().min(1).max(2000).optional(),
  loadingResponsibility: z.enum(LOOSE_CARGO_RESPONSIBILITIES).optional(),
  offloadingResponsibility: z.enum(LOOSE_CARGO_RESPONSIBILITIES).optional(),
  customerProvidesLabour: z.boolean().default(false),
  specialHandlingRequirementCodes: z.array(codeSchema).default([]),
  specialHandlingNotes: z.string().trim().min(1).max(500).optional(),
});
export type LooseCargoDetailInput = z.infer<typeof looseCargoDetailInputSchema>;

/** `POST /jobs/:id/drop-points`, `PATCH /jobs/:id/drop-points/:dropPointId` — one stop on a loose job's ordered route. */
export const dropPointInputSchema = z.object({
  zoneCode: codeSchema,
  contactName: z.string().trim().min(1).max(120),
  contactPhone: contactPhoneSchema,
});
export type DropPointInput = z.infer<typeof dropPointInputSchema>;

/** `PUT /jobs/:id/drop-points/order` — every stop's new position in one call, never one row at a time. */
export const reorderDropPointsSchema = z
  .object({ dropPoints: z.array(z.object({ id: z.string().uuid(), order: z.number().int().nonnegative() })).min(1) })
  .refine((v) => new Set(v.dropPoints.map((d) => d.order)).size === v.dropPoints.length, { message: 'Each stop needs a distinct order' })
  .refine((v) => new Set(v.dropPoints.map((d) => d.id)).size === v.dropPoints.length, { message: 'Each stop can appear only once' });
export type ReorderDropPointsInput = z.infer<typeof reorderDropPointsSchema>;

/** Admin special-handling-requirement catalogue (e.g. fragile, temperature-controlled) — same shape as `requiredDocumentTypeInputSchema`. */
export const specialHandlingRequirementInputSchema = z.object({
  code: z.string().trim().min(1).max(32),
  name: z.string().trim().min(1).max(120),
});
export type SpecialHandlingRequirementInput = z.infer<typeof specialHandlingRequirementInputSchema>;
